/**
 * Fetches only this section, so multiple collections can paginate independently
 * without changing the page URL or replacing cards the customer is using.
 */
class ParaInfiniteCollection extends HTMLElement {
  #grid;
  #next;
  #status;
  #sentinel;
  #observer;
  #controller;
  #loading = false;
  #paused = false;
  #generation = 0;
  #page = 1;
  #pages = new Set();

  connectedCallback() {
    this.#grid = this.querySelector('[data-infinite-grid]');
    this.#next = this.querySelector('[data-infinite-next]');
    this.#status = this.querySelector('[data-infinite-status]');
    this.#sentinel = this.querySelector('[data-infinite-sentinel]');
    this.#page = Number(this.dataset.page);
    this.#pages = new Set([this.#page]);
    this.#paused = false;
    this.#loading = false;

    if (!this.#grid || !this.#next || !this.#status || !this.#sentinel) return;
    if (!this.dataset.sectionId || !this.dataset.pageParam || !Number.isSafeInteger(this.#page) || this.#page < 1) {
      this.#showError();
      return;
    }

    // Section Rendering cannot include unsaved editor settings. Keep its first
    // batch as a layout preview and test pagination in the saved storefront.
    if (window.Shopify?.designMode) {
      this.#next.hidden = true;
      this.#setBusy(false);
      this.#setStatus(this.dataset.editorText || 'Save your changes and open Preview to test infinite scrolling.');
      return;
    }

    this.addEventListener('click', this.#handleClick);
    this.#setBusy(false);
    this.#next.hidden = !this.dataset.nextUrl;

    if (typeof IntersectionObserver === 'function') {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) void this.#loadNext(false);
        },
        { rootMargin: '240px 0px' }
      );
    }

    if (!this.dataset.nextUrl && this.#countItems() > 0) {
      this.#setStatus(this.dataset.completeText);
    }
    this.#observe();
  }

  disconnectedCallback() {
    // An old response must never update a section replaced by the theme editor.
    this.#generation += 1;
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#controller?.abort();
    this.#controller = undefined;
    this.#loading = false;
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('[data-infinite-next]') !== this.#next) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (!this.#loading) void this.#loadNext(true);
  };

  #observe() {
    if (this.dataset.autoLoad !== 'true' || this.#paused || this.#loading || !this.dataset.nextUrl || !this.isConnected) return;
    this.#observer?.observe(this.#sentinel);
  }

  #setBusy(busy) {
    this.setAttribute('aria-busy', String(busy));
    this.#grid?.setAttribute('aria-busy', String(busy));
    if (busy) this.#next?.setAttribute('aria-disabled', 'true');
    else this.#next?.removeAttribute('aria-disabled');
  }

  #countItems() {
    return Array.from(this.#grid?.children || []).filter((item) => item.matches('[data-infinite-item][data-product-id]')).length;
  }

  #setStatus(message) {
    if (this.#status) this.#status.textContent = (message || '').replaceAll('[count]', String(this.#countItems()));
  }

  #showError() {
    this.#paused = true;
    this.#observer?.disconnect();
    this.#setStatus(this.dataset.errorText || 'Unable to load more products. Please try again.');
  }

  #getNextUrl(value, currentPage) {
    if (!value) throw new Error('Missing next page');

    const next = new URL(value, window.location.href);
    const current = new URL(window.location.href);
    if (next.origin !== current.origin || next.pathname !== current.pathname) throw new Error('Invalid next page URL');

    const values = next.searchParams.getAll(this.dataset.pageParam);
    const page = Number(values[0]);
    if (values.length !== 1 || !/^\d+$/.test(values[0]) || !Number.isSafeInteger(page) || page <= currentPage) {
      throw new Error('Invalid next page number');
    }

    // Retain preview_theme_id, locale and other storefront parameters. Shopify
    // supplies the pagination parameter; it is not always named "page".
    for (const key of new Set(next.searchParams.keys())) {
      if (key === 'section_id' || key === 'sections') continue;
      current.searchParams.delete(key);
      for (const item of next.searchParams.getAll(key)) current.searchParams.append(key, item);
    }
    current.searchParams.delete('section_id');
    current.searchParams.delete('sections');
    current.hash = '';
    return { url: current, page };
  }

  async #loadNext(manual) {
    if (this.#loading || !this.dataset.nextUrl || !this.isConnected || (!manual && this.#paused)) return;

    this.#loading = true;
    this.#observer?.disconnect();
    this.#setBusy(true);
    this.#setStatus(this.dataset.loadingText || 'Loading products…');
    const generation = ++this.#generation;
    const controller = new AbortController();
    this.#controller = controller;
    const timeout = window.setTimeout(() => controller.abort(), 20000);

    try {
      const requested = this.#getNextUrl(this.dataset.nextUrl, this.#page);
      if (this.#pages.has(requested.page)) throw new Error('Repeated page');
      const requestUrl = new URL(requested.url);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        signal: controller.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error('Section request failed');
      const html = await response.text();
      if (!this.isConnected || generation !== this.#generation) return;

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const matches = Array.from(documentFragment.querySelectorAll('para-infinite-collection')).filter(
        (section) => section.dataset.sectionId === this.dataset.sectionId
      );
      if (matches.length !== 1) throw new Error('Missing section response');
      const section = matches[0];
      if (
        Number(section.dataset.page) !== requested.page ||
        section.dataset.pageParam !== this.dataset.pageParam ||
        !section.hasAttribute('data-next-url')
      ) {
        throw new Error('Unexpected section page');
      }
      if (this.dataset.collectionId && section.dataset.collectionId !== this.dataset.collectionId) {
        throw new Error('Collection settings changed');
      }
      const incomingGrid = section.querySelector('[data-infinite-grid]');
      if (!incomingGrid) throw new Error('Missing product grid');

      // Validate the entire response before touching this section's cursor.
      const next = section.dataset.nextUrl ? this.#getNextUrl(section.dataset.nextUrl, requested.page) : null;
      if (next && this.#pages.has(next.page)) throw new Error('Repeated next page');
      const ids = new Set(
        Array.from(this.#grid.children).filter((item) => item.matches('[data-infinite-item][data-product-id]')).map((item) => item.dataset.productId)
      );
      const items = [];
      for (const item of incomingGrid.children) {
        if (!item.matches('[data-infinite-item][data-product-id]') || !/^\d+$/.test(item.dataset.productId)) {
          throw new Error('Invalid product response');
        }
        if (ids.has(item.dataset.productId)) continue;
        ids.add(item.dataset.productId);
        for (const image of item.querySelectorAll('img')) image.loading = 'lazy';
        items.push(item);
      }
      if (!items.length) throw new Error('No new products returned');

      const keepFocus = manual && (document.activeElement === this.#next || this.#next.contains(document.activeElement));
      this.#grid.append(...items);
      this.#page = requested.page;
      this.#pages.add(this.#page);
      this.dataset.page = String(this.#page);
      this.dataset.nextUrl = next ? next.url.toString() : '';
      if (next) this.#next.href = next.url.toString();
      this.#next.hidden = !next;
      this.#paused = false;
      this.#setStatus(next ? this.dataset.loadedText : this.dataset.completeText);
      if (!next && keepFocus) this.#status.focus({ preventScroll: true });
    } catch {
      if (this.isConnected && generation === this.#generation) this.#showError();
    } finally {
      window.clearTimeout(timeout);
      if (generation === this.#generation) {
        this.#controller = undefined;
        this.#loading = false;
        this.#setBusy(false);
        this.#observe();
      }
    }
  }
}

if (!customElements.get('para-infinite-collection')) {
  customElements.define('para-infinite-collection', ParaInfiniteCollection);
}
